/**
 * New-seed program rules the editor has to explain before the server enforces
 * them.
 *
 * Mirroring and misting are mutually exclusive in both directions: a misted
 * zone is skipped by the session planner, so a mirror following it would
 * simply never water. The server (`assertMirrorValid`) rejects the pair with a
 * 409, but only while the program is still going to happen. Programs are never
 * purged once they expire, so "has a program at all" is the wrong test in both
 * places: it would lock a zone out of mirroring forever over a bag of seed
 * sown last spring.
 */

import type { NewSeedProgram } from '@irrigation/core';

/**
 * Is this program live, meaning active or still upcoming?
 *
 * Mirrors the server's rule exactly: a program exists and its end date has not
 * passed. `todayLocalDate` is an ISO `YYYY-MM-DD` in the tenant's local
 * calendar (`toIsoDate(new Date())` on the client), never a UTC slice, so a
 * program ending today still counts as live for a user west of Greenwich.
 */
export function newSeedLive(program: NewSeedProgram | undefined, todayLocalDate: string): boolean {
  return program !== undefined && program.endDate >= todayLocalDate;
}

/** "Back Lawn", "Back Lawn and Side Strip", "Back Lawn, Side Strip, and Parkway". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * Why this zone can't start a new-seed program right now, or undefined when it
 * can. `names` are the zones that mirror this one: they follow its smart
 * schedule, and misting takes it off that schedule entirely, so they would go
 * dry for the length of the program.
 */
export function mirrorDependentSeedBlock(names: string[]): string | undefined {
  if (names.length === 0) return undefined;
  if (names.length === 1) {
    return `${names[0]} waters with this zone and would stop watering during the program. Unlink it first (it can run its own program).`;
  }
  return `${joinNames(names)} water with this zone and would stop watering during the program. Unlink them first (they can run their own programs).`;
}
